import { useAuth } from "../_components";
import { useEffect, useState } from "react";
import { useDemoOnboardingRealtime } from '@/hooks/use-demo-onboarding-realtime'
import { getEcho } from '@/lib/pusher'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector, rAPI } from '@app.raytd.com/store'

const ProfileLoader = ({ children }) => {
    const {refreshUser} = useAuth();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeProfile = async () => {
            console.debug('[ProfileLoader] Initializing profile and Echo connection');
            await refreshUser();
            console.debug('[ProfileLoader] User refreshed, importing pusher module');
            const module = await import('../../lib/pusher');
            const echoInstance = module.initializeEcho();
            if (echoInstance) {
                console.debug('[ProfileLoader] Echo initialized successfully', {
                    hasPusherKey: !!import.meta.env.VITE_PUSHER_APP_KEY,
                    hasPusherCluster: !!import.meta.env.VITE_PUSHER_APP_CLUSTER,
                    hasApiUrl: !!import.meta.env.VITE_API_URL,
                });
            } else {
                console.warn('[ProfileLoader] Echo initialization returned null');
            }
            setIsInitialized(true);
        };
        initializeProfile();
    }, [refreshUser]);

    // Always render ProfileLoaderWithRealtime so `children` keep the same
    // parent element type across the init transition. Previously we rendered
    // `children` bare while `!isInitialized` and then re-wrapped them once
    // init completed — that type change remounted the whole subtree on every
    // ProfileLoader mount, wiping child state (raytd/web#31). Readiness is now
    // passed as a prop; the notification subscription inside stays gated on it,
    // so it still attaches only after refreshUser + Echo init have run.
    return <ProfileLoaderWithRealtime ready={isInitialized}>{children}</ProfileLoaderWithRealtime>;
};

const ProfileLoaderWithRealtime = ({ children, ready = true }) => {
    useDemoOnboardingRealtime();
    
    // Centralized notification listener
    // Notifications are driven by the `/api/v1/notifications` endpoint (database is source of truth).
    // Real-time updates come via Laravel Echo's .notification() helper which listens for
    // BroadcastNotificationCreated events from Laravel's notification system.
    // When a notification is broadcast, it invalidates the cache and shows a transient toast.
    const dispatch = useAppDispatch();
    const userId = useAppSelector(s => s.auth.user?.user?.id);

    useEffect(() => {
        // Wait until ProfileLoader has finished refreshUser + Echo init before
        // subscribing — same ordering as the old isInitialized gate, minus the
        // subtree remount (raytd/web#31).
        if (!ready) {
            return;
        }
        console.debug('[ProfileLoaderWithRealtime] Effect triggered', { userId });
        if (!userId) {
            console.warn('[ProfileLoaderWithRealtime] No userId available, skipping Echo subscription');
            return;
        }

        const echo = getEcho();
        if (!echo) {
            console.warn('[ProfileLoaderWithRealtime] Notification listener: Echo not available');
            return;
        }

        const channelName = `user.${userId}`;
        console.debug('[ProfileLoaderWithRealtime] Subscribing to private channel', { channelName });
        const channel = echo.private(channelName);

        const handleNotification = (notification: any) => {
            console.debug('[ProfileLoaderWithRealtime] Received notification via Echo', {
                raw: notification,
                type: notification?.type,
                dataType: notification?.data?.type,
                hasDownloadUrl: !!(notification?.download_url || notification?.data?.download_url),
            });
            // Invalidate notifications cache to trigger refetch
            dispatch(rAPI.util.invalidateTags([{ type: 'Notifications', id: 'LIST' }]));
            
            // Invalidate active exports cache (job completed, remove from "In Progress")
            dispatch(rAPI.util.invalidateTags(['ActiveExports']));
            
            // Check if notification has a download URL (for PDF/Excel ready notifications)
            const downloadUrl = notification.download_url || notification.data?.download_url;
            const isReadyNotification = downloadUrl && (
                notification.type?.includes('PdfReady') || 
                notification.type?.includes('ExportReady') ||
                notification.data?.type === 'report_pdf_ready' ||
                notification.data?.type === 'report_export_ready'
            );
            
            // Show transient toast for immediate feedback
            if (isReadyNotification && downloadUrl) {
                // Show toast with download button for ready notifications
                toast.success(notification.title || 'Export Ready', {
                    description: notification.body || notification.message || 'Your export is ready for download.',
                    action: {
                        label: 'Download',
                        onClick: () => {
                            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                        },
                    },
                });
            } else {
                // Regular toast for other notifications
                toast(notification.title || 'Notification', {
                    description: notification.body || notification.message || 'You have a new notification',
                });
            }
        };

        channel.error((status: number) => {
            console.error(`[ProfileLoaderWithRealtime] Failed to subscribe to notification channel ${channelName}. Status: ${status}`);
        });
    
        // Use Laravel Echo's .notification() helper for notification broadcasts
        console.debug('[ProfileLoaderWithRealtime] Attaching .notification() handler');
        channel.notification(handleNotification);

        return () => {
            console.debug('[ProfileLoaderWithRealtime] Cleaning up Echo subscription', { channelName });
            try {
                channel.stopListening('notification');
                channel.stopListening('pusher:subscription_succeeded');
                channel.stopListening('pusher:subscription_error');
                echo.leave(channelName);
            } catch (cleanupError) {
                console.warn('[ProfileLoaderWithRealtime] Error during Echo cleanup', cleanupError);
            }
        };
    }, [ready, userId, dispatch]);

    return children;
};

export default ProfileLoader;